/**
 * useOffline — network status hook + sync trigger.
 *
 * Returns `isOnline` (boolean) and fires `syncNow()` whenever the device
 * transitions from offline → online. Components that show an offline badge
 * import this; the sync trigger is a side-effect of re-connection.
 */
import { useEffect, useState } from 'react'
import { syncNow } from './sync'

let _syncOnReconnect: (() => void) | null = null

/** Register the global reconnect handler once at app boot (called from main.tsx). */
export function registerReconnectSync() {
  if (_syncOnReconnect) return  // idempotent
  _syncOnReconnect = () => { syncNow().catch(() => {}) }
  window.addEventListener('online', _syncOnReconnect)
}

export function useOffline() {
  const [isOnline, setIsOnline] = useState(navigator.onLine)

  useEffect(() => {
    const up = () => setIsOnline(true)
    const down = () => setIsOnline(false)
    window.addEventListener('online', up)
    window.addEventListener('offline', down)
    return () => { window.removeEventListener('online', up); window.removeEventListener('offline', down) }
  }, [])

  return { isOnline }
}
